import DeleteIcon from "@mui/icons-material/Delete";
import * as React from "react";

import {
  AlertDialog,
  RobotoIconButton,
  RobotoTooltip,
} from "@/shared/components";
import { ActionRecord } from "@/shared/domain/actions";
import { useDeleteAction } from "@/shared/domain/actions/hooks";
import { OrgRecord } from "@/shared/domain/orgs";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

interface DeleteActionIconButtonProps {
  action: ActionRecord;
  onDelete: (action: ActionRecord) => void;
  org: OrgRecord;
}

/**
 * A trashcan icon button that deletes an action after confirmation when clicked.
 */
export default function DeleteActionIconButton({
  action,
  onDelete,
  org,
}: DeleteActionIconButtonProps) {
  const [alertDialogOpen, setAlertDialogOpen] = React.useState(false);

  const deleteActionMutation = useDeleteAction();

  const dialogAction = React.useCallback(async () => {
    try {
      await deleteActionMutation.mutateAsync({
        name: action.name,
        resourceOwnerId: org.org_id,
      });
      onDelete(action);
      setAlertDialogOpen(false); // close dialog on success
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      ErrorMonitoringService.captureError(error, { action });
    }
  }, [action, org.org_id, onDelete, deleteActionMutation]);

  return (
    <>
      <RobotoTooltip title="Delete Action">
        <span>
          <RobotoIconButton
            eventName="DeleteActionClicked"
            eventProperties={{ name: action.name }}
            onClick={() => setAlertDialogOpen(true)}
            disabled={deleteActionMutation.isPending}
          >
            <DeleteIcon color="secondary" />
          </RobotoIconButton>
        </span>
      </RobotoTooltip>

      <AlertDialog
        dialogOpen={alertDialogOpen}
        handleClose={() => setAlertDialogOpen(false)}
        dialogTitle="Delete Action"
        dialogText={`Are you sure you want to delete ${action.name}?`}
        dialogAction={dialogAction}
      />
    </>
  );
}
